import React, { useState, useEffect } from "react";
import {
  Fab,
  Box,
  List,
  ListItemButton,
  Typography,
  Menu,
  MenuItem,
  Badge
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { Outlet, useLocation } from "react-router-dom";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DashboardIcon from "@mui/icons-material/Dashboard";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CampaignIcon from '@mui/icons-material/Campaign';
import BuildIcon from "@mui/icons-material/Build";
import TerminalIcon from "@mui/icons-material/Terminal";
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import SupportTicketModel from "./SupportTicketModel";
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { getUnreadCountAnnouncementsSlice } from "../../redux/actions/getUnreadCount AnnouncementsSlice";
import { getUnreadAnnouncementCountEndpoint } from "../../common/endpoints";
import { useDispatch } from "react-redux";

const drawerWidth = 95;
const seaGreen = "#6AC9CE";

const MainContent = (props) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSecond, setAnchorElSecond] = useState(null);
  const [anchorElAdmin, setAnchorElAdmin] = useState(null); // Added state for Admin submenu
  const [showSupportModal, setShowSupportModal] = useState(false);
  const roleDetails = localStorage.getItem("userRole");
  const isAppAdmin = roleDetails === "App Admin";
  const isSupplier = roleDetails === "Supplier Planner" || roleDetails === "Supplier Admin";
  const userEmail = localStorage.getItem("userEmail");
  const dispatch = useDispatch();
  const [unreadCount, setUnreadCount] = useState(0)

  const handleMenuClick = (item, event) => {
    if (item.label === "Planning") {
      setAnchorEl(event.currentTarget);
    }
    if (item.label === "Announcements") {
      props.handleMenuItemClick(item.path, item.label, item);
    }
    else if (item.label === "Admin") {

      if (isAppAdmin) {
        //  APP_ADMIN → open submenu
        setAnchorElAdmin(event.currentTarget);
      }
      else {
        //  Other users → directly go to Manage Users page
        props.handleMenuItemClick(
          "/admin/manage-users",
          "Manage Users",
          { path: "/admin/manage-users", label: "Manage Users" }
        );
      }

    }
    else {
      props.handleMenuItemClick(item.path, item.label, item);
    }
  };

  const handleSubmenuClose = () => setAnchorEl(null);
  const handleSecondSubmenuClose = () => setAnchorElSecond(null);
  const handleAdminSubmenuClose = () => setAnchorElAdmin(null); // Close Admin submenu

  useEffect(() => {
    fetchUnreadCount();

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 15000); // refresh every 15 sec

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (event) => {
      const menuEls = document.querySelectorAll(".MuiMenu-paper");

      // check if click is inside any open menu
      const clickedInsideMenu = Array.from(menuEls).some((menu) =>
        menu.contains(event.target)
      );

      // check if click is inside the sidebar button (so menu toggle still works)
      const clickedOnAnchor =
        anchorEl?.contains(event.target) ||
        anchorElSecond?.contains(event.target) ||
        anchorElAdmin?.contains(event.target); // Check for Admin anchor

      if (!clickedInsideMenu && !clickedOnAnchor) {
        setAnchorEl(null);
        setAnchorElSecond(null);
        setAnchorElAdmin(null); // Close Admin menu
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [anchorEl, anchorElSecond, anchorElAdmin]);

  const fetchUnreadCount = async () => {
    try {
      const response = await dispatch(
        getUnreadCountAnnouncementsSlice({
          dynamicEndpoint: getUnreadAnnouncementCountEndpoint,
        })
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        setUnreadCount(response.payload?.unreadCount || 0);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderNavButton = (item) => {
    const isActive =
      location.pathname === item.path ||
      location.pathname.startsWith(item.path + "/");
    const ICON_MAP = {
      dashboard: DashboardIcon,
      planning: InsertChartIcon,
      poExceptionManagement: SyncAltOutlinedIcon,
      reports: SummarizeIcon,
      admin: SupervisorAccountIcon,
      kpimetrics: TrackChangesIcon,
      alerts: NotificationsIcon,
      announcements: CampaignIcon,
      adminTraining: BuildIcon,
      training: TerminalIcon,
    };
    const IconComponent = ICON_MAP[item.icon]
    return (
      <ListItemButton
        key={item.label}
        onClick={(e) => handleMenuClick(item, e)}
        disabled={item.disabled}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          backgroundColor: isActive ? seaGreen : "",
          color: isActive ? "white" : "black",
          "&:hover": { backgroundColor: seaGreen },
          // mb: 0.5,
          // py: 1,
          p: 1
        }}
      >
        <Box sx={{ color: isActive ? "#fff" : "#000", mt: 0.4 }}>
          {item.label === "Announcements" && isSupplier ? (
            <Badge
              badgeContent="NEW"
              color="error"
              invisible={unreadCount === 0}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "8px",
                  minWidth: "22px",
                  height: "14px",
                },
              }}
            >
              <IconComponent />
            </Badge>
          ) : (
            IconComponent && <IconComponent />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: 0.3,
            color: isActive ? "#fff" : "#000",
            textAlign: "center",
            mt: 0.5,
            textDecoration: item?.redirect ? "underline" : "none",
          }}
        >
          {item.label}
        </Typography>
      </ListItemButton>
    );
  };

  // Determine if sidebar should be hidden
  const hideSidebarRoutes = ["/landing-page", "/login", "/"];
  const hideSidebar = hideSidebarRoutes.includes(location.pathname);
  const isCPSActive = location.pathname === "/forecastCollab-cps";
  const isDPSActive = location.pathname === "/forecastCollab-dps";

  const handleAddLTSupportClick = () => {
    setShowSupportModal(true);
  }

  return (
    <Box sx={{ display: "flex" }}>
      {/* Sidebar - show only if not landing page */}
      {!hideSidebar && (
        <Box
          sx={{
            width: drawerWidth,
            backgroundColor: "#ddd",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,

          }}
        >
          {/* Menu */}
          <Box sx={{ width: "100%" }}>
            <List sx={{ width: "100%" }}>
              {props.menuItems?.map((item) => renderNavButton(item))}
            </List>
          </Box>

          {/* Bottom link */}
          {isSupplier && <Box
            sx={{
              textAlign: "center",
              mt: "auto",
              width: "100%",
              px: 1,
              pb: 5,
            }}
          >
            <Box
              component="a"
              href={process.env.REACT_APP_POCOLLABORATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",   // 👈 IMPORTANT (prevents layout break)
                alignItems: "center",
                justifyContent: "center",
                width: "100%",            // keeps full clickable area
                color: "black",
                fontWeight: 500,
                py: "8px",
                fontSize: "12px",
                lineHeight: "16px",
                textDecoration: "underline",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: seaGreen,

                },
              }}
            >
              Redirect to SAP Ariba PO collaboration
            </Box>
          </Box>}
        </Box>
      )}

      {/* Submenu for Planning */}
      {!hideSidebar && anchorEl && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleSubmenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              backgroundColor: "#ddd",
              color: "#000",
              border: "1px solid #000",
              boxShadow: "none",
              "& .MuiMenuItem-root": {
                fontSize: "13px",
                paddingY: "8px",
                paddingX: "24px",
                minWidth: "120px",
                minHeight: "50px",
                border: "1px solid transparent", // default no visible border
                "&:hover": {
                  // border: "1px solid #000", // show black border on hover
                  backgroundColor: "transparent", // no color change
                },
              },
            },
          }}

        >
          <MenuItem onClick={(e) => setAnchorElSecond(e.currentTarget)} sx={{ mx: 0.5 }}>
            <Typography sx={{ fontSize: "16px" }}>
              Forecast
            </Typography>
            <KeyboardArrowRightIcon fontSize="small" />
          </MenuItem>
          <Menu
            anchorEl={anchorElSecond}
            open={Boolean(anchorElSecond)}
            onClose={handleSecondSubmenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                backgroundColor: "#ddd",
                color: "#000",
                border: "1px solid #000",
                boxShadow: "none",
                mt: -1,
                ml: 0.5,
                "& .MuiMenuItem-root": {
                  fontSize: "13px",
                  paddingY: "6px",
                  paddingX: "20px",
                  minWidth: "130px",
                  minHeight: "50px",
                  border: "1px solid transparent", // default no border
                  transition: "border-color 0.1s ease",
                  "&:hover": {
                    border: "1.5px solid #000", // show black border on hover
                    // backgroundColor: "#ddd", // keep same background

                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                props.handleMenuItemClick(
                  "/forecastCollab-cps",
                  "Planning",
                  { path: "/forecastCollab-cps", label: "Forecast" }
                );
                setAnchorEl(null);
                setAnchorElSecond(null);
              }}
              sx={{
                mx: 0.5, backgroundColor: isCPSActive ? seaGreen : "",
                color: isCPSActive ? "white" : "black",
                "&:hover": {
                  backgroundColor: isCPSActive ? seaGreen : "",
                  color: isCPSActive ? "white" : "black",
                },
              }}
            >
              CPS
            </MenuItem>
            <MenuItem
              onClick={() => {
                props.handleMenuItemClick(
                  "/forecastCollab-dps",
                  "Planning",
                  { path: "/forecastCollab-dps", label: "Forecast" }
                );
                setAnchorEl(null);
                setAnchorElSecond(null);
              }}
              sx={{
                mx: 0.5, backgroundColor: isDPSActive ? seaGreen : "",
                color: isDPSActive ? "white" : "black",
                "&:hover": {
                  backgroundColor: isDPSActive ? seaGreen : "",
                  color: isDPSActive ? "white" : "black",
                },
              }}
            >
              DPS
            </MenuItem>
          </Menu>
        </Menu>
      )}

      {/* Submenu for Admin */}
      {!props.hideSidebar && isAppAdmin && anchorElAdmin && (
        <Menu
          anchorEl={anchorElAdmin}
          open={Boolean(anchorElAdmin)}
          onClose={handleAdminSubmenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              color: "#000",
              border: "1px solid #000",
              boxShadow: "none",
              "& .MuiMenuItem-root": {
                fontSize: "13px",
                paddingY: "2px",
                paddingX: "18px",
                minWidth: "120px",
                minHeight: "40px",
                border: "1px solid transparent",
                // "&:hover": {
                //   backgroundColor: "transparent",
                // },
              },
            },
          }}
        >
          {/* MenuItem for "Manage Users" */}
          <MenuItem
            component="div"
            onClick={() => {
              props.handleMenuItemClick(
                "/admin/manage-users",
                "Manage Users",
                { path: "/admin/manage-users", label: "Manage Users" }
              );
              setAnchorElAdmin(null);
            }}
            sx={{
              "&:hover": {
                backgroundColor:
                  location.pathname === "/admin/manage-users"
                    ? "#6AC9CE"   // ⬅️ keep same if active
                    : "#f0f0f0", // ⬅️ hover only when inactive
              },
              color: location.pathname === "/admin/manage-users" ? "white" : "black", // Active background
              backgroundColor: location.pathname === "/admin/manage-users" ? "#6AC9CE" : "transparent", // Active background
            }}
          >
            Manage Users
          </MenuItem>

          {/* MenuItem for "Manage Reason Codes" */}
          <MenuItem
            component="div"
            onClick={() => {
              props.handleMenuItemClick(
                "/admin/manage-reason-codes",
                "Admin",
                { path: "/admin/manage-reason-codes", label: "Admin" }
              );
              setAnchorElAdmin(null);
            }}
            sx={{
              "&:hover": {
                backgroundColor:
                  location.pathname === "/admin/manage-reason-codes"
                    ? "#6AC9CE"
                    : "#f0f0f0",
              },
              backgroundColor: location.pathname === "/admin/manage-reason-codes" ? "#6AC9CE" : "transparent", // Active background
              color: location.pathname === "/admin/manage-reason-codes" ? "white" : "black", // Active background
            }}
          >
            Manage Reason Codes
          </MenuItem>

          {/* Add other admin-related menu items here */}
        </Menu>
      )}

      {/* Content */}
      <Box
        sx={{
          // marginLeft: hideSidebar ? 0 : `${drawerWidth}px`,
          flex: 1,
          paddingLeft: 2,
        }}
      >
        <Outlet />
      </Box>
      {/* <div className="content">
        <div>

          {true && (
            <Fab
              variant="extended"
              disabled={location.pathname === "/ticketsummary"}
              style={{
                position: "fixed",
                bottom: "5px",
                left: "2px",
                zIndex: 1000,
                background: `${(location.pathname === "/ticketsummary") ? '#0000001f' : "#64cac6"}`,
                color: "#fff",
                // padding: '10px 10px',
                margin:'10px 0 0 10px',
                boxShadow: "0px 3px 5px -1px rgba(0,0,0,1.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)"
              }}
              onClick={handleAddLTSupportClick} 
            >
              <AddIcon sx={{ mr: 1 }} />
              Support
            </Fab>
          )}

        </div>
        <SupportTicketModel
          showModal={showSupportModal}
          title="Support Ticket"
          handleClose={() => setShowSupportModal(false)}
          userEmail={userEmail}
        // selectedItem={selectedItem}
        />
      </div> */}
    </Box>
  );
};

export default MainContent;
